import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Video, 
  User, 
  Briefcase, 
  Calendar, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  Play,
  Eye,
  Filter,
  FileText,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { toast } from 'react-toastify';
import Backdrop from 'components/Backdrop';
import { getVideoTestReviews } from 'api/apiHelper';
import { VideoTestSession } from 'types/video-test-types';

const VideoTestReviewList: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [testSessions, setTestSessions] = useState<VideoTestSession[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [expandedNotes, setExpandedNotes] = useState<number | null>(null);

  const toggleNotes = (sessionId: number) => {
    setExpandedNotes(expandedNotes === sessionId ? null : sessionId);
  };

  useEffect(() => {
    fetchTestSessions();
  }, [statusFilter]);

  const fetchTestSessions = async () => {
    setLoading(true);
    try {
      const response = await getVideoTestReviews(statusFilter);
      setTestSessions(response.data);
    } catch (error: any) {
      console.error('Error fetching video test sessions:', error);
      toast.error('Failed to load video test submissions');
    } finally {
      setLoading(false);
    }
  };

  const handleViewResponses = (testToken: string) => {
    navigate(`/careers/job-application/video-test-result/${testToken}`);
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { bg: string; text: string; icon: React.ReactNode }> = {
      completed: { 
        bg: 'bg-emerald-500/20', 
        text: 'text-emerald-400', 
        icon: <CheckCircle className="w-4 h-4" /> 
      },
      in_progress: { 
        bg: 'bg-amber-500/20', 
        text: 'text-amber-400', 
        icon: <Clock className="w-4 h-4" /> 
      },
      pending: { 
        bg: 'bg-slate-500/20', 
        text: 'text-slate-400', 
        icon: <AlertCircle className="w-4 h-4" /> 
      },
      expired: { 
        bg: 'bg-red-500/20', 
        text: 'text-red-400', 
        icon: <AlertCircle className="w-4 h-4" /> 
      },
    };
    
    const badge = badges[status] || badges.pending;
    
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.icon}
        {status.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <Backdrop message="Loading video test submissions..." />;
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-navy-700 dark:text-white">
            Video Test Submissions
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Review video responses from candidates
          </p>
        </div>
        
        {/* Filter */}
        <div className="flex items-center gap-3">
          <Filter className="w-5 h-5 text-gray-500" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 bg-white dark:bg-navy-700 border border-gray-200 dark:border-navy-600 rounded-xl text-sm text-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500"
          >
            <option value="">All Status</option>
            <option value="completed">Completed</option>
            <option value="in_progress">In Progress</option>
            <option value="pending">Pending</option>
            <option value="expired">Expired</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-navy-800 rounded-2xl shadow-lg overflow-hidden">
        {testSessions.length === 0 ? (
          <div className="p-12 text-center">
            <Video className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
              No Video Test Submissions
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              Video test submissions will appear here once candidates complete their assessments.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-navy-700">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Candidate
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Position
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Completed At
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Videos
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Notes
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-navy-600">
                {testSessions.map((session) => (
                  <React.Fragment key={session.id}>
                    <tr className="hover:bg-gray-50 dark:hover:bg-navy-700 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="flex items-center justify-center w-10 h-10 bg-brand-500/10 rounded-full mr-3">
                            <User className="w-5 h-5 text-brand-500" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {session.applicant_name}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {session.applicant_email}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <Briefcase className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-gray-700 dark:text-gray-300">
                            {session.job_title}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(session.status)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center text-gray-600 dark:text-gray-400">
                          <Calendar className="w-4 h-4 mr-2" />
                          {formatDate(session.completed_at)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <Video className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-gray-700 dark:text-gray-300">
                            {session.video_responses?.length || 0} / {session.total_questions}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => toggleNotes(session.id)}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                            session.reviewer_notes
                              ? expandedNotes === session.id
                                ? 'bg-brand-500 text-white'
                                : 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-500/30'
                              : 'bg-gray-100 dark:bg-navy-600 text-gray-400 cursor-default'
                          }`}
                          disabled={!session.reviewer_notes}
                        >
                          <FileText className="w-4 h-4" />
                          {session.reviewer_notes ? (
                            expandedNotes === session.id ? (
                              <ChevronUp className="w-4 h-4" />
                            ) : (
                              <ChevronDown className="w-4 h-4" />
                            )
                          ) : (
                            <span className="text-xs">None</span>
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => handleViewResponses(session.test_token)}
                          disabled={session.status !== 'completed'}
                          className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                            session.status === 'completed'
                              ? 'bg-brand-500 hover:bg-brand-600 text-white'
                              : 'bg-gray-100 dark:bg-navy-600 text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          View Responses
                        </button>
                      </td>
                    </tr>
                    {/* Expandable Notes Row */}
                    {expandedNotes === session.id && session.reviewer_notes && (
                      <tr className="bg-gray-50 dark:bg-navy-700/50">
                        <td colSpan={7} className="px-6 py-4">
                          <div className="flex items-start gap-3">
                            <div className="flex items-center justify-center w-8 h-8 bg-brand-500/10 rounded-lg flex-shrink-0 mt-0.5">
                              <FileText className="w-4 h-4 text-brand-500" />
                            </div>
                            <div className="flex-1">
                              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                                Reviewer Notes
                              </h4>
                              <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                                {session.reviewer_notes}
                              </p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoTestReviewList;

